import { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Sparkles, Box, Cpu, Calculator } from "lucide-react";
import { BRAND } from "@/lib/data";

// Priority tools shown as highlighted pills — always visible on desktop
const TOOLS = [
  { label: "AI Studio",  to: "/ai-studio",    icon: Cpu,        testid: "nav-ai-studio" },
  { label: "3D Kitchen", to: "/configurator", icon: Box,        testid: "nav-3d-kitchen" },
  { label: "Estimator",  to: "/estimate",     icon: Calculator, testid: "nav-estimator" },
];

const LINKS = [
  { label: "Home", to: "/" },
  { label: "Gallery", to: "/gallery" },
  { label: "Pricing", to: "/pricing" },
  { label: "Journal", to: "/blog" },
  { label: "Commercial", to: "/commercial" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const loc = useLocation();
  const nav = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [loc.pathname]);

  return (
    <header
      data-testid="navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-[background,backdrop-filter,border-color] duration-500 ${
        scrolled ? "bg-[#F9F8F6]/80 backdrop-blur-xl border-b border-[#143D63]/10" : "bg-transparent border-b border-transparent"
      }`}
    >
      {/* Top utility strip — the "power tools" always visible on desktop */}
      <div className="hidden md:block bg-[#143D63] text-[#F9F8F6]">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-8 flex items-center justify-between h-9">
          <div className="flex items-center gap-1 -ml-1">
            <span className="text-[10px] tracking-[0.2em] font-bold text-[#F8C21C] uppercase mr-3">Try free →</span>
            {TOOLS.map((t) => {
              const Icon = t.icon;
              const active = loc.pathname === t.to;
              return (
                <Link
                  key={t.to}
                  to={t.to}
                  data-testid={t.testid}
                  className={`inline-flex items-center gap-1.5 px-3 py-1 text-[11px] font-bold tracking-wide transition-colors ${
                    active ? "bg-[#F8C21C] text-[#143D63]" : "text-[#F9F8F6] hover:text-[#F8C21C]"
                  }`}
                >
                  <Icon size={12} /> {t.label}
                </Link>
              );
            })}
          </div>
          <div className="text-[11px] text-[#F9F8F6]/70">
            <a href="tel:+919051293651" className="hover:text-[#F8C21C]" data-testid="nav-phone">+91 90512 93651</a>
            <span className="mx-3 text-[#F9F8F6]/30">·</span>
            <span>Rajdanga Main Rd, Kolkata</span>
          </div>
        </div>
      </div>

      <div className="max-w-[1400px] mx-auto px-5 sm:px-8 flex items-center justify-between h-24">
        <Link to="/" data-testid="navbar-logo" className="flex items-center gap-3">
          <img src={BRAND.logo} alt={BRAND.full} className="h-16 sm:h-20 w-auto object-contain" />
        </Link>

        <nav className="hidden md:flex items-center gap-8">
          {LINKS.map((l) => (
            <Link
              key={l.to}
              to={l.to}
              data-testid={`nav-${l.label.toLowerCase().replace(" ", "-")}`}
              className={`link-underline text-sm font-medium tracking-wide ${
                loc.pathname === l.to ? "text-[#8B0000]" : "text-[#143D63]"
              }`}
            >
              {l.label}
            </Link>
          ))}
          {/* Highlighted tool pills right in the main nav */}
          <div className="flex items-center gap-2 border-l border-[#143D63]/15 pl-6">
            {TOOLS.map((t) => {
              const Icon = t.icon;
              const active = loc.pathname === t.to;
              return (
                <Link
                  key={t.to}
                  to={t.to}
                  data-testid={`${t.testid}-pill`}
                  className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-[11px] font-bold tracking-wide transition-colors ${
                    active
                      ? "bg-[#F8C21C] text-[#143D63]"
                      : "bg-[#F8C21C]/15 text-[#143D63] border border-[#F8C21C]/60 hover:bg-[#F8C21C]"
                  }`}
                >
                  <Icon size={12} /> {t.label}
                </Link>
              );
            })}
          </div>
        </nav>

        <div className="hidden md:flex items-center gap-3">
          <Link
            to="/studio"
            data-testid="nav-my-studio-pill"
            className="inline-flex items-center gap-1.5 rounded-full bg-[#8B0000]/10 border border-[#8B0000]/40 text-[#8B0000] px-4 py-2 text-xs font-bold tracking-wide hover:bg-[#8B0000] hover:text-[#F9F8F6] transition-colors"
          >
            <Sparkles size={13} /> My Studio
          </Link>
          <button
            data-testid="nav-book-btn"
            onClick={() => nav("/consultation")}
            className="bg-[#F8C21C] text-[#143D63] px-5 py-2.5 text-xs font-bold tracking-wide hover:bg-[#143D63] hover:text-[#F9F8F6] transition-colors duration-300"
          >
            Book Free Consultation
          </button>
        </div>

        <button
          className="md:hidden text-[#143D63]"
          data-testid="nav-mobile-toggle"
          onClick={() => setOpen((o) => !o)}
          aria-label="Menu"
        >
          {open ? <X size={26} /> : <Menu size={26} />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
            className="md:hidden overflow-hidden bg-[#F9F8F6] border-t border-[#143D63]/10"
          >
            <div className="px-5 py-6 flex flex-col gap-4">
              {/* Priority tools — top of mobile menu */}
              <div className="grid grid-cols-3 gap-2">
                {TOOLS.map((t) => {
                  const Icon = t.icon;
                  return (
                    <Link
                      key={t.to}
                      to={t.to}
                      data-testid={`${t.testid}-mobile`}
                      className="flex flex-col items-center justify-center gap-1 py-3 border border-[#F8C21C]/60 bg-[#F8C21C]/15 text-[#143D63] text-[11px] font-bold"
                    >
                      <Icon size={16} /> {t.label}
                    </Link>
                  );
                })}
              </div>
              {LINKS.map((l) => (
                <Link key={l.to} to={l.to} className="text-lg font-serif text-[#143D63]">
                  {l.label}
                </Link>
              ))}
              <Link
                to="/studio"
                data-testid="nav-mobile-my-studio"
                className="inline-flex items-center gap-2 rounded-full border border-[#8B0000]/60 bg-[#8B0000]/10 text-[#8B0000] px-4 py-2 text-sm font-bold w-fit"
              >
                <Sparkles size={14} /> My Studio
              </Link>
              <button
                data-testid="nav-mobile-book-btn"
                onClick={() => nav("/consultation")}
                className="bg-[#F8C21C] text-[#143D63] px-6 py-3 font-bold text-sm w-full"
              >
                Book Free Consultation
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
